import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useAuth } from '../../hooks/useAuth';
import { regenerateAISummary } from '../../services/reportService';
import { parseAiSummary, getRecommendationLabel } from '../../utils/aiSummaryUtils';
import showNotification from '../common/Notification';
import AICommentSettingsDialog from './AICommentSettingsDialog';

const CONFIDENCE_CONFIG = {
  high: { label: 'Yüksek', color: 'success' },
  medium: { label: 'Orta', color: 'warning' },
  low: { label: 'Düşük', color: 'error' },
};

const RECOMMENDATION_COLORS = {
  apply: 'success',
  reject: 'error',
  retest: 'warning',
  monitor: 'info',
  pending: 'default',
};

/**
 * Executive AI summary card — structured CRO insights with regenerate action
 */
const AISummaryCard = ({ reportId, aiSummary, metricIndex = 0, onUpdated, readOnly = false, report }) => {
  const { user } = useAuth();
  const canEdit = !readOnly && (user?.role === 'admin' || user?.role === 'editor');
  const parsed = useMemo(() => parseAiSummary(aiSummary), [aiSummary]);
  const [generating, setGenerating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleGenerate = useCallback(async (options = {}) => {
    if (!reportId || !canEdit) return;

    try {
      setGenerating(true);
      const result = await regenerateAISummary(reportId, metricIndex, options);
      const structured = result.data;
      onUpdated?.(JSON.stringify(structured));
      showNotification('AI özeti oluşturuldu', 'success');
      setDialogOpen(false);
    } catch {
      showNotification('AI özeti oluşturulamadı', 'error');
    } finally {
      setGenerating(false);
    }
  }, [reportId, canEdit, metricIndex, onUpdated]);

  const confidence = CONFIDENCE_CONFIG[parsed?.confidence] || CONFIDENCE_CONFIG.medium;
  const recColor = RECOMMENDATION_COLORS[parsed?.recommendation] || 'default';

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        p: 2.5,
        border: '1px solid #eee',
        borderRadius: 2,
        bgcolor: '#fafafa',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: parsed ? 2 : 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon sx={{ color: '#240B4B', fontSize: 22 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#240B4B' }}>
            AI Executive Özet
          </Typography>
        </Box>

        {canEdit && (
          <Button
            size="small"
            variant={parsed ? 'outlined' : 'contained'}
            onClick={() => setDialogOpen(true)}
            disabled={generating}
            startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            sx={{
              borderColor: '#240B4B',
              color: parsed ? '#240B4B' : '#fff',
              bgcolor: parsed ? 'transparent' : '#240B4B',
              '&:hover': {
                bgcolor: parsed ? 'rgba(36,11,75,0.06)' : '#3d1c6b',
                borderColor: '#240B4B',
              },
            }}
          >
            {generating ? 'Oluşturuluyor...' : parsed ? 'Yeniden oluştur' : 'AI Özeti Oluştur'}
          </Button>
        )}
      </Box>

      {!parsed && !canEdit && (
        <Typography variant="body2" color="text.secondary">
          Henüz AI özeti oluşturulmamış.
        </Typography>
      )}

      {!parsed && canEdit && !generating && (
        <Typography variant="body2" color="text.secondary">
          Test sonuçlarına göre CRO odaklı executive özet oluşturmak için butona tıklayın.
        </Typography>
      )}

      {parsed && (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'text.primary' }}>
            {parsed.summary}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {parsed.recommendation && (
              <Chip
                label={`Öneri: ${getRecommendationLabel(parsed.recommendation)}`}
                color={recColor}
                size="small"
                variant="outlined"
              />
            )}
            {parsed.confidence && (
              <Chip
                label={`Güven: ${confidence.label}`}
                color={confidence.color}
                size="small"
                variant="outlined"
              />
            )}
          </Box>

          {parsed.risks?.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Riskler
              </Typography>
              <List dense disablePadding>
                {parsed.risks.map((risk, index) => (
                  <ListItem key={index} disableGutters sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={risk}
                      primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      )}
      <AICommentSettingsDialog
        open={dialogOpen}
        onClose={() => !generating && setDialogOpen(false)}
        report={report}
        metricIndex={metricIndex}
        onGenerate={handleGenerate}
        generating={generating}
      />
    </Paper>
  );
};

export default AISummaryCard;
